import React, { useState } from "react";
import {
  ArrowRightLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Info,
  Users,
  XCircle
} from "lucide-react";
import { Switch } from "../../../../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../../../../ui/dialog";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";
import {
  explainBookingMove,
  getExplanationDetails
} from "../../../../../services/ai/SpaceOptimiserAI";
import { ProcessedScanResult } from "../SpaceScanner.types";
import { getAreaNames, getReasonText } from "./SpaceScannerStage2Controller";

type SpaceScannerStage2BookingCardProps = {
  details: ProcessedScanResult;
  onToggle: (bookingId: string, checked: boolean) => void;
};

const MoreInfoSection = ({ details }: { details: ProcessedScanResult }) => {
  const [isOpen, setIsOpen] = useState(false);
  const detailLines = getExplanationDetails(details);

  if (detailLines.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-[var(--borderColor)] pt-2">
      <button
        type="button"
        className="flex w-full items-center gap-1 text-xs font-medium text-[var(--primaryColor)] hover:underline"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <ChevronUp className="size-3" />
        ) : (
          <ChevronDown className="size-3" />
        )}
        More info
      </button>
      {isOpen && (
        <ul className="mt-1.5 max-h-32 list-disc space-y-1 overflow-y-auto pl-4 text-[var(--secondaryColor)]">
          {detailLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SpaceScannerStage2BookingCard = ({
  details,
  onToggle
}: SpaceScannerStage2BookingCardProps) => {
  const { isFlagEnabled } = useFeatureFlagContext();
  const showAICoPilot = isFlagEnabled("showAICoPilot");
  const customerName = `${details.booking.first_name} ${details.booking.last_name}`;
  const partySize = details.booking.num_people;
  const fromArea = getAreaNames(details.previousAreas) || "Unassigned";
  const toArea = getAreaNames(details.newAreas) || "Unassigned";
  const isMoving = details.moved && !details.shuffleLocked;
  const isLocked = details.shuffleLocked;
  const reasonText = getReasonText(details.result);

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-[var(--borderColor)] p-2.5 sm:p-3">
      <div className="flex flex-1 items-start gap-3 min-w-0">
        <div className="flex-shrink-0 pt-0.5">
          {isMoving ? (
            <ArrowRightLeft className="size-5 text-[var(--primaryColor,#3b82f6)]" />
          ) : (
            <XCircle className="size-5 text-[var(--secondaryColor,#6b7280)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="mb-1 flex flex-col gap-0.5 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-2">
            <span className="text-[13px] font-semibold capitalize text-[var(--primaryColor)] sm:text-sm">
              {customerName}
            </span>
            <span className="text-xs whitespace-nowrap text-[var(--secondaryColor)]">
              {partySize} guest{partySize !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1 text-xs text-[var(--primaryColor)] sm:text-[13px]">
            {isMoving ? (
              <>
                <span className="text-[var(--secondaryColor)]">{fromArea}</span>
                <span className="mx-0.5 text-[var(--secondaryColor)]">
                  &rarr;
                </span>
                <span className="font-medium text-[var(--primaryColor,#3b82f6)]">
                  {toArea}
                </span>
              </>
            ) : (
              <span className="text-[var(--secondaryColor)]">
                {isLocked
                  ? `Will be left in ${fromArea}`
                  : `Will stay in ${fromArea}`}
              </span>
            )}
          </div>
          {!isMoving && reasonText && (
            <div className="mt-1 text-xs italic text-[var(--secondaryColor)]">
              {reasonText}
            </div>
          )}
          {showAICoPilot && (
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="mt-1.5 flex items-center gap-1 text-xs text-[var(--primaryColor,#3b82f6)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primaryColor,#3b82f6)] focus-visible:ring-offset-1 rounded"
                >
                  <Info className="size-3.5" />
                  <span>AI insight</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-sm bg-[var(--panelBackground)] text-[var(--primaryColor)]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-sm">
                    <Info className="size-4" />
                    AI Insight
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-xs leading-relaxed">
                  <div className="flex items-center gap-2">
                    <Users className="size-3.5 flex-shrink-0 text-[var(--secondaryColor)]" />
                    <span>
                      <strong>{customerName}</strong> &mdash; {partySize} guest
                      {partySize !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {details.previousAreas?.[0] && (
                    <div className="rounded border border-[var(--borderColor)] p-2">
                      <span className="font-medium">Current area: </span>
                      {details.previousAreas[0].name}
                      {details.previousAreaCapacity != null && (
                        <span className="text-[var(--secondaryColor)]">
                          {" "}
                          (seats {details.previousAreaCapacity})
                        </span>
                      )}
                    </div>
                  )}
                  {isMoving && details.newAreas?.[0] && (
                    <div className="rounded border border-[var(--borderColor)] bg-[var(--pageBackgroundSecondary)] p-2">
                      <span className="font-medium">Suggested area: </span>
                      {details.newAreas[0].name}
                      {details.areaCapacity != null && (
                        <span className="text-[var(--secondaryColor)]">
                          {" "}
                          (seats {details.areaCapacity})
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-[var(--secondaryColor)]">
                    {explainBookingMove(details)}
                  </p>
                  <MoreInfoSection details={details} />
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      <div className="flex min-h-6 flex-shrink-0 items-center">
        {!details.completed && (
          <Switch
            disabled={details.shuffleLocked}
            name={`booking-${details.booking._id}`}
            checked={details.moved}
            onCheckedChange={(checked) => {
              onToggle(details.booking._id, checked);
            }}
          />
        )}
        {details.completed && details.moved && (
          <CheckCircle2 className="size-5 text-[var(--progressLow,#22c55e)]" />
        )}
      </div>
    </div>
  );
};
